import { Component } from 'vue-property-decorator'
import throttle from 'lodash/throttle'
import debounce from 'lodash/debounce'
import MoleculeModel from '@/models/molecule'
import { Collection } from 'collect.js'
import molecule from './molecule/index.vue'
import background from './background/index.vue'
import { mixins } from 'vue-class-component'
import MixinGame from '@/mixins/game'
import { boardWidthOriginal, boardHeightOriginal } from '@constants/board'
import { ZoomEvent } from '@/interfaces/events'
import events from '@shared/events'

@Component({
  components: {
    'vgb-molecule': molecule,
    'vgb-board-background': background,
  },
})
export default class Game extends mixins(MixinGame) {
  private xBoardOnMouseDown: number = 0
  private yBoardOnMouseDown: number = 0
  private xMouseOnMouseDown: number = 0
  private yMouseOnMouseDown: number = 0

  private lineWidth: number = 1
  private classObject: object = {}

  get styleObject(): object {
    return {
      transform: `translate(${this.boardX}px, ${this.boardY}px) scale(${this.boardZoomFactor})`,
    }
  }

  get boardElement(): HTMLElement {
    return this.$refs.board as HTMLElement
  }

  get viewportElement(): HTMLElement {
    return this.$refs.viewport as HTMLElement
  }

  get minZoomFactor(): number {
    return this.getViewportRect().width / this.getBoardRect().width
  }

  get molecules(): Collection<MoleculeModel> {
    return this.$molecules.all()
  }

  public mounted() {
    this.init()
    this.debounceSetLineWidth = debounce(this.setLineWidth, 100)
    this.debounceSetClassObject = debounce(this.setClassObject, 50)

    this.$feathers.service('api/viewport')
      .on('updated', (viewport) => {
        if (!this.yourTurn) {
          this.setBoardX(viewport.x)
          this.setBoardY(viewport.y)
          this.setBoardZoomFactor(viewport.zoom)
        }
      })
  }

  public onMouseDown(event: any) {
    event.preventDefault()

    if (event.touches) {
      event = event.touches[0]
    }

    this.xBoardOnMouseDown = this.boardX
    this.yBoardOnMouseDown = this.boardY
    this.xMouseOnMouseDown = event.clientX
    this.yMouseOnMouseDown = event.clientY

    window.addEventListener('touchend', this.onMouseUp)
    window.addEventListener('mouseup', this.onMouseUp)
    window.addEventListener('mousemove', this.onMouseMove)
    window.addEventListener('touchmove', this.onMouseMove)
  }

  private getBoardRect(): ClientRect {
    return this.boardElement.getBoundingClientRect()
  }

  private getViewportRect(): ClientRect {
    return this.viewportElement.getBoundingClientRect()
  }

  private init() {
    this.bindEvents()
  }

  private bindEvents() {
    this.$el.addEventListener('wheel', throttle(this.onMouseWheel, 24))

    this.$bus.$on(events.BOARD_ZOOM, (event: ZoomEvent) => {
      this.onZoom({
        factor: this.minZoomFactor + ((1 - this.minZoomFactor) * event.factor),
        x: event.x,
        y: event.y,
      })
    })
  }

  private onMouseUp() {
    window.removeEventListener('touchend', this.onMouseUp)
    window.removeEventListener('mouseup', this.onMouseUp)
    window.removeEventListener('mousemove', this.onMouseMove)
    window.removeEventListener('touchmove', this.onMouseMove)
  }

  private onMouseMove(event: any) {
    if (event.touches) {
      event = event.touches[0]
    }

    const deltaX: number = this.xMouseOnMouseDown - event.clientX
    const deltaY: number = this.yMouseOnMouseDown - event.clientY

    const x = this.constrainX(this.xBoardOnMouseDown - deltaX, this.getBoardRect().width)
    const y = this.constrainY(this.yBoardOnMouseDown - deltaY, this.getBoardRect().height)

    this.setBoardX(x)
    this.setBoardY(y)
    this.broadcastViewport()
  }

  private broadcastViewport() {
    this.$feathers.service('api/viewport')
      .update(this.tableID, {
        x: this.boardX,
        y: this.boardY,
        zoom: this.boardZoomFactor,
      })
  }

  private onMouseWheel(event: any) {
    const deltaZoom: number = event.deltaY * .005
    const newZoomFactor = this.constrainZoom(this.boardZoomFactor + deltaZoom)

    this.onZoom({
      factor: newZoomFactor,
      x: event.clientX,
      y: event.clientY,
    })
  }

  private onZoom(event: ZoomEvent) {
    const widthBefore = boardWidthOriginal * this.boardZoomFactor
    const widthAfter = boardWidthOriginal * event.factor

    const heightBefore = boardHeightOriginal * this.boardZoomFactor
    const heightAfter = boardHeightOriginal * event.factor

    const mouseBoardFactorX = (event.x + Math.abs(this.getBoardRect().left)) / this.getBoardRect().width
    const mouseBoardFactorY = (event.y + Math.abs(this.getBoardRect().top)) / this.getBoardRect().height

    const correctX = (widthBefore - widthAfter) * mouseBoardFactorX
    const correctY = (heightBefore - heightAfter) * mouseBoardFactorY

    const x = this.constrainX(this.boardX + correctX, widthAfter)
    const y = this.constrainY(this.boardY + correctY, heightAfter)

    this.setBoardX(x)
    this.setBoardY(y)
    this.setBoardZoomFactor(event.factor)
    this.debounceSetLineWidth()
    this.debounceSetClassObject()
    this.broadcastViewport()
  }

  private debounceSetLineWidth(): void {
    // placeholder
  }

  private debounceSetClassObject(): void {
    // placeholder
  }

  private setClassObject(): any {
    this.classObject = {
      // 'hide-labels': this.boardZoomFactor < .35,
      'hide-labels': false,
    }
  }

  private setLineWidth(): any {
    this.lineWidth = Math.round((1 / this.boardZoomFactor) * 1e2) / 1e2
  }

  private constrainX(value: number, newWidth: number) {
    value = Math.min(value, 0)
    value = Math.max(value, this.getViewportRect().width - newWidth)

    return value
  }

  private constrainY(value: number, newHeight: number) {
    value = Math.min(value, 0)
    value = Math.max(value, this.getViewportRect().height - newHeight)

    return value
  }

  private constrainZoom(value: number) {
    value = Math.min(value, 1)
    value = Math.max(value, this.minZoomFactor)

    return value
  }
}
